import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { isServerError } from "../../../config/api";
import { cpfOrCnpjMask, normalizeCpfOrCnpj } from "../../../helpers/Masks";
import { clearSessionData, isMobileDevice } from "../../../helpers/Utils";
import BalcaoVirtualService from "../../../services/BalcaoVirtualService";
import ConsultarService from "../../../services/ConsultarService";
import Button from "../../Button";
import Input, { MaterialInputEvent } from "../Input";

import style from "./styles.module.scss";

const keyI18n = "translations.virtualDesk.contributorForm";

interface Props {
  onHide?(): void;
  desklink: string;
  attendant: string;
  balcaoId?: number;
  stopReload(): void;
}

type Feedback = "not-found" | "server-error" | null;

const isValidDocument = (document: string) => (
  document.length === 14 || document.length === 18
);

export default function ConsultModal({
  onHide,
  desklink,
  attendant,
  balcaoId,
  stopReload,
}: React.PropsWithChildren<Props>) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [documents, setDocuments] = useState<string[]>([""]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const formValid = useMemo(
    () => name.trim().length >= 3 && isValidDocument(documents[0]),
    [documents, name],
  );

  useEffect(() => {
    stopReload();
  }, []);

  const handleNameInput = (event: MaterialInputEvent) => {
    setName(event.currentTarget.value);
  };

  const handleDocumentInput = (
    index: number,
    event: MaterialInputEvent,
  ) => {
    const nextDocuments = [...documents];
    nextDocuments[index] = event.currentTarget.value;
    setDocuments(nextDocuments);
  };

  const addDocument = () => {
    setDocuments((currentDocuments) => [...currentDocuments, ""]);
  };

  const removeDocument = (index: number) => {
    setDocuments((currentDocuments) => (
      currentDocuments.filter((_, documentIndex) => documentIndex !== index)
    ));
  };

  const registerAttendance = async (contributorDocument: string) => {
    await ConsultarService.attendanceRegistration({
      contributor_document: contributorDocument,
      contributor_attendance_histories: {
        attendance_place: "Balcão Virtual",
        attendant,
        attendance_subject: "attendment_at_the_counter",
      },
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!formValid || loading) {
      return;
    }

    const contributorDocument = normalizeCpfOrCnpj(documents[0]);
    const normalizedDocuments = documents
      .filter(isValidDocument)
      .map((document) => normalizeCpfOrCnpj(document));

    setFeedback(null);
    setLoading(true);

    try {
      const contributorStatus = await ConsultarService.contributorStatus({
        contributor_document: contributorDocument,
      });

      await registerAttendance(contributorDocument);

      if (!balcaoId) {
        setLoading(false);
        return;
      }

      await ConsultarService.contributor({
        contributor_document: contributorDocument,
      });

      const response = await BalcaoVirtualService.changeStatus(
        "busy",
        balcaoId,
        "",
        normalizedDocuments,
        contributorStatus.contributor_name,
        name.trim(),
      );

      clearSessionData();
      localStorage.setItem("document", contributorDocument);

      if (!isMobileDevice()) {
        localStorage.setItem("attendance_id", `${response.data.attendance_id}`);
        localStorage.setItem("documents", JSON.stringify(normalizedDocuments));
      }

      window.location.href = `video_call/${balcaoId}/${desklink.split("/").pop()}`;
    } catch (error) {
      setLoading(false);

      if (isServerError(error)) {
        setFeedback("not-found");
        return;
      }

      setFeedback("server-error");
    }
  };

  return (
    <section
      id="updatecontributormodal"
      className={style.modal}
      aria-labelledby="contributor-modal-title"
    >
      <div className={style.header}>
        <h2 id="contributor-modal-title" className={`pgm-title-large ${style.title}`}>
          {t(`${keyI18n}.title`)}
        </h2>
        <Button
          variant="text"
          size="extra-small"
          iconButton
          type="button"
          aria-label={t(`${keyI18n}.aria.close`)}
          onClick={onHide}
          icon={<span className="material-symbols-sharp">close</span>}
        />
      </div>

      {feedback && (
        <div className={`${style.feedback} pgm-body-medium`} role="alert">
          <span className="material-symbols-sharp" aria-hidden="true">
            {feedback === "not-found" ? "search_off" : "error"}
          </span>
          <span>
            {feedback === "not-found"
              ? t(`${keyI18n}.errors.notFound`)
              : t(`${keyI18n}.errors.server`)}
          </span>
        </div>
      )}

      <div className={style.content}>
        <span className={`material-symbols-sharp ${style.userIcon}`} aria-hidden="true">
          account_circle
        </span>

        <p className={`pgm-body-medium ${style.initialMessage}`}>
          {t(`${keyI18n}.description`)}
        </p>

        <form className={style.form} autoComplete="off" onSubmit={handleSubmit}>
          <Input
            variant="filled"
            type="text"
            label={t(`${keyI18n}.fields.name`)}
            value={name}
            required
            fullWidth
            onInput={handleNameInput}
          />

          <div className={style.documents}>
            {documents.map((document, index) => (
              <div className={style.documentRow} key={index}>
                <Input
                  variant="filled"
                  type="text"
                  label={index === 0
                    ? t(`${keyI18n}.fields.document`)
                    : t(`${keyI18n}.fields.additionalDocument`, { number: index })}
                  value={document}
                  placeholder={t(`${keyI18n}.fields.documentPlaceholder`)}
                  inputMode="numeric"
                  mask={cpfOrCnpjMask}
                  required={index === 0}
                  fullWidth
                  onInput={(event) => handleDocumentInput(index, event)}
                />

                {index > 0 && (
                  <Button
                    variant="text"
                    size="extra-small"
                    iconButton
                    type="button"
                    tooltip={t(`${keyI18n}.actions.removeDocument`)}
                    aria-label={t(`${keyI18n}.aria.removeDocument`, { number: index })}
                    onClick={() => removeDocument(index)}
                    icon={<span className="material-symbols-sharp">delete</span>}
                  />
                )}
              </div>
            ))}
          </div>

          <div className={style.actions}>
            <Button
              variant="outlined"
              size="extra-small"
              type="button"
              onClick={addDocument}
              icon={<span className="material-symbols-sharp">add</span>}
            >
              {t(`${keyI18n}.actions.addDocument`)}
            </Button>

            <Button
              variant="tonal"
              size="small"
              type="submit"
              disabled={!formValid || loading}
              icon={loading
                ? <span className={`material-symbols-sharp ${style.loadingIcon}`}>progress_activity</span>
                : undefined}
            >
              {t(`${keyI18n}.actions.${loading ? "loading" : "continue"}`)}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
